import React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

/**
 * A render-time crash inside a page used to take the whole panel down silently: React
 * unmounts the subtree, the DOM goes blank, and — because the crash happens during
 * render, not in an event handler — no toast or banner is ever reached. That is exactly
 * what QA reported for Migration Architect ("panel reset to its blank initial state, no
 * toast, no banner, no error text anywhere in the DOM"), and there was no error boundary
 * anywhere in the app to catch it.
 *
 * Asana "Bug: Migration Architect — Analyze session silently resets" (QA 2026-08-04).
 *
 * Deliberately generic and self-contained: it renders the error instead of a blank
 * screen, and offers a reset that re-mounts the subtree without a full page reload so
 * unrelated app state survives.
 */

type Props = {
  children: React.ReactNode;
  /** Shown in the heading, e.g. "Jarvis Intelligence". */
  label?: string;
};

type State = { error: Error | null };

class PageErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the stack in the console — the QA pass found a *clean* console, which made
    // the failure impossible to diagnose from the browser.
    console.error('[PageErrorBoundary] Render failed:', error, info.componentStack);
  }

  handleReset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="p-8 flex items-start justify-center">
        <div className="max-w-xl w-full bg-white border border-red-200 rounded-xl shadow-sm p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-red-700">
                {this.props.label || 'This page'} hit an unexpected error
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Nothing was lost on the server — any analysis already running keeps going and
                will appear in Session History. Try again, and if it keeps happening send this
                message to the team.
              </p>
              <pre className="mt-3 text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 text-gray-700 whitespace-pre-wrap break-words">
                {error.message || String(error)}
              </pre>
              <button
                onClick={this.handleReset}
                className="mt-4 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-800"
              >
                <RotateCcw className="w-4 h-4" />
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default PageErrorBoundary;
